import React, { useEffect, useState, useMemo, useCallback } from 'react'
import App from './App'
import { useAuth } from "./api/authStore"
import { loginUrl } from './config/config'
import ApiClient, {getCfgByOrigin} from "./api/api"


const AppWrapper = (props) => {
    // let params = new URLSearchParams(document.location.search)
    // let token = params.get("token")

    // console.log('Token'+token)

    let path = window.location.pathname.replace(/\//g, "")
    
    // Мемоизируем apiClient, чтобы он не пересоздавался при каждом рендере
    const apiClient = useMemo(() => new ApiClient(getCfgByOrigin()), [])

    const { user, getAccessTokenSilently, logout } = useAuth()
    const [userData, setUserData] = useState(null)
    const [activeProxies, seActiveProxies] = useState([])
    const [reloadActive, setReloadActive] = React.useState(false)
    const [reloadData, setReloadData] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(false)
    // const [hasLoadedData, setHasLoadedData] = React.useState(false)
    const isLoadingRef = React.useRef(false)
    const hasLoadedDataRef = React.useRef(false)
    const abortControllerRef = React.useRef(null)

    // Логирование только в режиме разработки
    React.useEffect(() => {
        if (userData && process.env.NODE_ENV === 'development') {
            console.log('AppWrapper: User data loaded and ready')
        }
    }, [userData])
    const loadUserData = useCallback(async (forceReload = false) => {
        // Защита от множественных вызовов
        if (isLoadingRef.current) {
            if (process.env.NODE_ENV === 'development') {
                console.log('API call already in progress, skipping...')
            }
            return
        }
        
        // Дополнительная защита - если данные уже загружены и не требуется принудительная перезагрузка
        if (hasLoadedDataRef.current && !forceReload) {
            if (process.env.NODE_ENV === 'development') {
                console.log('User data already loaded, skipping initial load...')
            }
            return
        }
        
        // Отменяем предыдущий запрос, если он существует
        if (abortControllerRef.current) {
            abortControllerRef.current.abort()
        }
        
        // Создаем новый AbortController для отмены запросов
        const abortController = new AbortController()
        abortControllerRef.current = abortController
        
        try {
            isLoadingRef.current = true
            setIsLoading(true)
            const token = getAccessTokenSilently()
            if (!token) {
                if (process.env.NODE_ENV === 'development') {
                    console.log('No access token available, redirecting to login')
                }
                hasLoadedDataRef.current = false
                logout(loginUrl)
                return
            }
            
            if (process.env.NODE_ENV === 'development') {
                console.log('Loading user data...')
            }
            const userDataResponse = await apiClient.setToken(token).getUserData(null, abortController.signal)
            if (process.env.NODE_ENV === 'development') {
                console.log('API getUserData response:', userDataResponse)
            }
            setUserData(userDataResponse)
            
            const activeResponse = await apiClient.setToken(token).loacActiveProxies(abortController.signal)
            seActiveProxies(activeResponse)
            if (process.env.NODE_ENV === 'development') {
                console.log('Active proxies loaded:', activeResponse)
            }

            // Устанавливаем флаг, что данные загружены
            hasLoadedDataRef.current = true

            // Небольшая задержка перед сбросом флагов, чтобы избежать немедленного reload
            setTimeout(() => {
                setReloadActive(false)
                setReloadData(false)
            }, 100)
        } catch (e) {
            // Игнорируем ошибки отмены запросов
            if (e.name !== 'AbortError' && e.code !== 'ECONNABORTED' && e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') {
                if (process.env.NODE_ENV === 'development') {
                    console.error('Error loading user data:', e)
                }
                // Если ошибка аутентификации, перенаправляем на логин
                if (e.status === 401 || e.status === 403) {
                    hasLoadedDataRef.current = false
                    logout(loginUrl)
                }
            }
        } finally {
            isLoadingRef.current = false
            setIsLoading(false)
        }
    }, [getAccessTokenSilently, logout, apiClient])

    useEffect(()=>{
        if (!user) {
          if (path !== 'login' && path !== 'signup' && path !== 'passwdrecovery'){
            if (process.env.NODE_ENV === 'development') {
                console.log('No user token, redirecting to login:', loginUrl)
            }
            hasLoadedDataRef.current = false
            logout(loginUrl)
          } 
        } else if (!hasLoadedDataRef.current) {
            loadUserData()
        }
    },[user, path, logout, loadUserData])
    
    // Отдельный useEffect для reload флагов
    React.useEffect(() => {
        if (user && (reloadActive || reloadData)) {
            if (process.env.NODE_ENV === 'development') {
                console.log('Reload triggered:', { reloadActive, reloadData })
            }
            // Используем единую функцию loadUserData с флагом принудительной перезагрузки
            loadUserData(true)
        }
    }, [reloadActive, reloadData, user, loadUserData])
    
    // Очистка при размонтировании компонента
    React.useEffect(() => {
        return () => {
            // Отменяем только если компонент действительно размонтируется
            if (abortControllerRef.current && !isLoadingRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [])
    
    return (
        <>
          <App userData={userData} active={activeProxies} apiClient={apiClient} setReloadActive={setReloadActive} setReloadData={setReloadData}/>
        </>
    )
}

export default AppWrapper